import axios from 'axios';
import { getApiBaseUrl, getBackendUrl } from '@/config/apiBase';

const getStoredToken = () => localStorage.getItem('token');

export const isEmailVerificationRequiredError = (error) => {
  const detail = error?.response?.data?.detail || error?.message || '';
  return (
    (error?.response?.status === 403 || !error?.response) &&
    typeof detail === 'string' &&
    detail.toLowerCase().includes('verify your email')
  );
};

export const getApiErrorDetail = (error) => error?.response?.data?.detail;

export const getApiErrorCode = (error) => {
  const detail = getApiErrorDetail(error);
  return detail && typeof detail === 'object' ? detail.code || null : null;
};

export const getApiErrorMessage = (error, fallbackMessage = 'Something went wrong') => {
  const detail = getApiErrorDetail(error);

  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }

  if (detail && typeof detail === 'object' && typeof detail.message === 'string' && detail.message.trim()) {
    return detail.message;
  }

  if (typeof error?.message === 'string' && error.message.trim()) {
    return error.message;
  }

  return fallbackMessage;
};

// Create axios instance with interceptors
const apiClient = axios.create();

// Request interceptor to add auth header
apiClient.interceptors.request.use(
  (config) => {
    config.baseURL = getApiBaseUrl();
    const token = getStoredToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const detail = error.response?.data?.detail || '';
      // Only redirect for actual token issues, not login failures
      if ((detail.includes('expired') || detail.includes('Invalid token')) && 
          !window.location.pathname.includes('/login')) {
        localStorage.removeItem('token');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export const api = {
  // Auth - Password Reset
  forgotPassword: async (email) => {
    const response = await apiClient.post('/auth/forgot-password', { email });
    return response.data;
  },

  resetPassword: async (token, newPassword) => {
    const response = await apiClient.post('/auth/reset-password', { 
      token, 
      new_password: newPassword 
    });
    return response.data;
  },

  verifyEmail: async (token) => {
    const response = await apiClient.get('/auth/verify-email', {
      params: { token }
    });
    return response.data;
  },

  resendVerification: async () => {
    const response = await apiClient.post('/auth/resend-verification', {});
    return response.data;
  },

  // Resume Analysis
  analyzeResume: async (resumeText, jobDescription = null) => {
    const response = await apiClient.post('/resume/analyze', { 
      resume_text: resumeText, 
      job_description: jobDescription 
    });
    return response.data;
  },

  uploadResume: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post('/resume/upload', formData, { 
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  getGuidedResumeActiveSession: async () => {
    const response = await apiClient.get('/guided-resume/active-session');
    return response.data;
  },

  startGuidedResumeSession: async (payload) => {
    const response = await apiClient.post('/guided-resume/start', payload);
    return response.data;
  },

  getGuidedResumeSession: async (sessionId) => {
    const response = await apiClient.get(`/guided-resume/sessions/${sessionId}`);
    return response.data;
  },

  submitGuidedResumeFollowUp: async (sessionId, payload) => {
    const response = await apiClient.post(`/guided-resume/sessions/${sessionId}/follow-up`, payload);
    return response.data;
  },

  generateGuidedResume: async (sessionId, payload) => {
    const response = await apiClient.post(`/guided-resume/sessions/${sessionId}/generate`, payload);
    return response.data;
  },

  listSavedResumes: async () => {
    const response = await apiClient.get('/saved-resumes');
    return response.data;
  },

  getSavedResume: async (savedResumeId) => {
    const response = await apiClient.get(`/saved-resumes/${savedResumeId}`);
    return response.data;
  },

  createSavedResume: async (payload) => {
    const response = await apiClient.post('/saved-resumes', payload);
    return response.data;
  },

  deleteSavedResume: async (savedResumeId) => {
    const response = await apiClient.delete(`/saved-resumes/${savedResumeId}`);
    return response.data;
  },

  compareResume: async (resumeText, jobDescription) => {
    const response = await apiClient.post('/resume/compare', { 
      resume_text: resumeText, 
      job_description: jobDescription 
    });
    return response.data;
  },

  generateResume: async (jobDescription, currentResume = null, skills = null) => {
    const response = await apiClient.post('/resume/generate', {
      job_description: jobDescription,
      current_resume: currentResume,
      skills
    });
    return response.data;
  },

  generateCoverLetter: async (resumeText, jobDescription) => {
    const response = await apiClient.post('/cover-letter/generate', {
      resume_text: resumeText,
      job_description: jobDescription
    });
    return response.data;
  },

  exportCoverLetter: async (coverLetterContent, format = 'docx') => {
    const response = await apiClient.post('/cover-letter/export', {
      cover_letter_content: coverLetterContent,
      format
    });
    return response.data;
  },

  rewriteBullets: async (bulletPoints, jobDescription = null) => {
    const response = await apiClient.post('/resume/rewrite-bullets', { 
      bullet_points: bulletPoints, 
      job_description: jobDescription 
    });
    return response.data;
  },

  exportResume: async (
    resumeContent,
    format = 'docx',
    resumeData = null,
    guidedResumeData = null,
    guidedResumeExport = false,
    guidedResumeResultStatus = null,
  ) => {
    const response = await apiClient.post('/resume/export', { 
      resume_content: resumeContent, 
      resume_data: resumeData,
      guided_resume_data: guidedResumeData,
      guided_resume_export: guidedResumeExport,
      guided_resume_result_status: guidedResumeResultStatus,
      format 
    });
    return response.data;
  },

  // Subscription
  getPlans: async () => {
    const response = await apiClient.get('/plans');
    return response.data;
  },

  createCheckout: async (plan) => {
    const formData = new FormData();
    formData.append('plan', plan);
    const response = await apiClient.post('/checkout/create', formData);
    return response.data;
  },

  getCheckoutStatus: async (sessionId) => {
    const response = await apiClient.get(`/checkout/status/${sessionId}`);
    return response.data;
  },

  cancelSubscription: async () => {
    const response = await apiClient.post('/subscription/cancel', {});
    return response.data;
  },

  // History & Usage
  getHistory: async (limit = 10) => {
    const response = await apiClient.get(`/history?limit=${limit}`);
    return response.data;
  },

  getUsage: async () => {
    const response = await apiClient.get('/usage');
    return response.data;
  },

  trackPageView: async (path) => {
    const response = await apiClient.post('/analytics/page-view', { path });
    return response.data;
  },

  getPublishedResourcePage: async (slug) => {
    const response = await apiClient.get(`/resource-pages/${slug}`);
    return response.data;
  },

  listPublishedResourcePages: async () => {
    const response = await apiClient.get('/resource-pages');
    return response.data;
  },

  // Admin
  getAdminStats: async () => {
    const response = await apiClient.get('/admin/stats');
    return response.data;
  },

  getAdminAnalytics: async () => {
    const response = await apiClient.get('/admin/analytics');
    return response.data;
  },

  searchAdminUsers: async (query = '', limit = 20) => {
    const response = await apiClient.get('/admin/users', {
      params: { query, limit }
    });
    return response.data;
  },

  getAdminBetaAccounts: async (query = '') => {
    const response = await apiClient.get('/admin/beta-accounts', {
      params: query ? { query } : {}
    });
    return response.data;
  },

  createAdminBetaAccount: async (payload) => {
    const response = await apiClient.post('/admin/beta-accounts', payload);
    return response.data;
  },

  createAdminBetaUser: async (payload) => {
    const response = await apiClient.post('/admin/beta-users', payload);
    return response.data;
  },

  updateAdminBetaAccount: async (userId, payload) => {
    const response = await apiClient.put(`/admin/beta-accounts/${userId}`, payload);
    return response.data;
  },

  removeAdminBetaAccount: async (userId) => {
    const response = await apiClient.delete(`/admin/beta-accounts/${userId}`);
    return response.data;
  },

  getAdminResourcePages: async () => {
    const response = await apiClient.get('/admin/resource-pages');
    return response.data;
  },

  createAdminResourcePage: async (payload) => {
    const response = await apiClient.post('/admin/resource-pages', payload);
    return response.data;
  },

  updateAdminResourcePage: async (pageId, payload) => {
    const response = await apiClient.put(`/admin/resource-pages/${pageId}`, payload);
    return response.data;
  },

  setAdminResourcePagePublished: async (pageId, published) => {
    const response = await apiClient.patch(`/admin/resource-pages/${pageId}/publish`, { published });
    return response.data;
  },

  deleteAdminResourcePage: async (pageId) => {
    const response = await apiClient.delete(`/admin/resource-pages/${pageId}`);
    return response.data;
  },

  // Account
  deleteAccount: async (currentPassword) => {
    const response = await apiClient.delete('/account', {
      data: {
        current_password: currentPassword
      }
    });
    return response.data;
  },

  changePassword: async (currentPassword, newPassword) => {
    const response = await apiClient.post('/account/change-password', { 
      current_password: currentPassword, 
      new_password: newPassword 
    });
    return response.data;
  },

  downloadProtectedFile: async (path) => {
    const token = getStoredToken();
    const response = await fetch(`${getBackendUrl()}${path}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });

    if (!response.ok) {
      let detail = 'Download failed';
      try {
        const data = await response.json();
        detail = data?.detail || detail;
      } catch (error) {
        // Ignore non-JSON responses and surface the generic error.
      }
      throw new Error(detail);
    }

    return response.blob();
  }
};
